import { memo, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useClerk, useSession } from "@clerk/react";
import { useSignIn, useSignUp } from "@clerk/react/legacy";
import { BodyCopy } from "../components/BodyCopy";
import {
  Button,
  GradientText,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { ErrorAlertIcon as ErrorIcon } from "../assets/icons";
import { AuraLayout } from "../components/AuraLayout";
import { FloatingField } from "../components/FloatingField";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { SocialAuthButton } from "../components/consumer-auth/SocialAuthButton";
import {
  OTP_LENGTH,
  OtpCodeInput,
} from "../components/consumer-auth/OtpCodeInput";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import {
  bindConsumerIdentity,
  getOperation,
  getWidgetApiError,
} from "../services/api";
import { isAxiosNetworkFailure } from "../utils/axiosNetworkError";
import { getClerkConsumerPublishableKey } from "../auth/clerkPublishableKey";
import { authErrorMessage } from "../auth/clerkErrors";
import {
  AUTH_ERROR_PARAM,
  AUTH_FROM_PARAM,
  AUTH_RESET_PARAM,
  buildAuthPageUrl,
  isAuthBindErrorReturn,
  isAuthFromClaim,
  ssoCallbackUrl,
  BIND_SUBJECT_BLOCKED_CODE,
  parseAuthResume,
  resolvePostAuthDestination,
  subjectBlockedTerminalState,
} from "../auth/resume";
import {
  applyConsumerIdentityBind,
  clearConsumerIdentityBind,
} from "../auth/consumerIdentityCache";
import {
  bindingMessageKey,
  clearAuthMode,
  persistAuthMode,
  readAuthMode,
  type AuthMode,
} from "../auth/authMode";
import { resendEmailOtp, startEmailSignInOrUp } from "../auth/startEmailAuth";
import {
  formatResendClock,
  useOtpResendCooldown,
} from "../auth/otpResendCooldown";
import { isClaimable } from "../utils/claimGate";
import { shouldShowWelcomeBefore, welcomePagePath } from "../utils/termsGate";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { useVisualViewportPin } from "../hooks/useVisualViewportPin";
import { Navbar } from "../components/Navbar";

type AuthStep = "form" | "code";

export const AuthPage = memo(() => {
  const publishableKey = getClerkConsumerPublishableKey();
  if (!publishableKey) {
    return <AuthMissingKey />;
  }
  return <AuthPageInner />;
});

export const AuthMissingKey = () => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { redirectTo } = useRouteWithToken();
  const colorConfig = theme.errorView;
  const {
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    iconColors,
    backgroundIcon,
  } = colorConfig;

  return (
    <AuraLayout colorConfig={colorConfig}>
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark backgroundIcon={backgroundIcon}>
            <ErrorIcon size={60} colors={iconColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t("error.title")}
              highlightColor={title}
              secudnary=""
              secudnaryColor={title}
            />
            <BodyCopy textColor={bodyCopy}>{t("auth.missingKey")}</BodyCopy>
          </section>
        </section>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          <Button
            size="large"
            background={backgroundBtn}
            color={textColorBtn}
            onClick={() => redirectTo(APP_ROUTES.CLAIM_ENTRY_PAGE)}
          >
            {t("common.retry")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
};

const AuthPageInner = memo(() => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { redirectTo, linkId } = useRouteWithToken();
  const [searchParams] = useSearchParams();
  // First-paint snapshot: Clerk cookieless URL sync must not rewrite `resume`
  // into an unknown value (parseAuthResume treats those as transactions).
  const resumeRawRef = useRef(searchParams.get("resume"));
  const fromClaimRef = useRef(isAuthFromClaim(searchParams.get(AUTH_FROM_PARAM)));
  const clerk = useClerk();
  const { isLoaded: sessionLoaded, session: clerkSession } = useSession();
  const { isLoaded: signInLoaded, signIn, setActive: setSignInActive } =
    useSignIn();
  const { isLoaded: signUpLoaded, signUp, setActive: setSignUpActive } =
    useSignUp();

  const [mode, setMode] = useState<AuthMode>(() => readAuthMode());
  const [step, setStep] = useState<AuthStep>("form");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(() =>
    isAuthBindErrorReturn(searchParams.get(AUTH_ERROR_PARAM))
      ? t("auth.errorBind")
      : null,
  );
  const [binding, setBinding] = useState(false);
  const resetPendingRef = useRef(false);
  const bindFailedRef = useRef(false);
  const verifyCtaRef = useRef<HTMLElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const keyboardProxyRef = useRef<HTMLInputElement>(null);
  useVisualViewportPin();
  const {
    remaining: resendRemaining,
    canResend,
    start: startResendCooldown,
    reset: resetResendCooldown,
  } = useOtpResendCooldown();
  const codeCompleteRef = useRef(false);

  // Completing the code hands focus to the CTA, which also drops the OTP ring.
  // Runs after commit so the button is already enabled, and only on the
  // transition, so a code that is already full steals nothing on re-render.
  useEffect(() => {
    const complete = code.length === OTP_LENGTH;
    if (complete && !codeCompleteRef.current) {
      verifyCtaRef.current?.querySelector("button")?.focus();
    }
    codeCompleteRef.current = complete;
  }, [code]);

  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    footerColor,
    errorColor,
  } = colorConfig;
  const fieldColors = {
    background,
    title,
    bodyCopy,
    inactiveColor: colorConfig.subtitile,
    errorColor,
  };
  const errorInk = errorColor ?? TITLE_COLOR;

  const ready = sessionLoaded && signInLoaded && signUpLoaded;
  const sessionStatus = clerkSession?.status;

  /**
   * Post-bind navigation. Welcome wraps class B/C destinations when terms are
   * pending. Unavailable is never wrapped.
   */
  const continueTo = (path: string) => {
    // Read after bind: claim snapshot is stale (isolated links have no User).
    const sessionNow = useSessionStore.getState().session.data;
    if (shouldShowWelcomeBefore(sessionNow, path)) {
      redirectTo(welcomePagePath(path));
      return;
    }
    redirectTo(path);
  };

  const finishBind = async () => {
    if (!clerkSession || binding) return;
    setBinding(true);
    setError(null);
    try {
      const token = await clerkSession.getToken();
      if (!token) {
        throw new Error("missing_token");
      }
      const bound = await bindConsumerIdentity(token);
      applyConsumerIdentityBind({
        clerkSessionId: clerkSession.id,
        linkId,
        bound,
      });
      clearAuthMode();

      const resume = parseAuthResume(resumeRawRef.current);
      const dest = resolvePostAuthDestination({
        resume,
        occupancy: bound.occupancy,
        takeable: isClaimable(session.data?.config),
      });

      if (resume.kind === "receipt" || resume.kind === "ofac") {
        try {
          if (linkId) {
            await getOperation(linkId, resume.operationId);
          }
          continueTo(dest);
        } catch {
          continueTo(APP_ROUTES.TRANSACTIONS_PAGE);
        }
        return;
      }

      continueTo(dest);
    } catch (bindError) {
      console.error("[AuthPage] Consumer identity bind failed:", bindError);
      if (isAxiosNetworkFailure(bindError)) {
        setBinding(false);
        return;
      }
      if (getWidgetApiError(bindError)?.code === BIND_SUBJECT_BLOCKED_CODE) {
        redirectTo(APP_ROUTES.ERROR_PAGE, {
          replace: true,
          state: subjectBlockedTerminalState(t),
        });
        return;
      }
      await signOutAfterBindFailure();
    }
  };

  const authPageUrl = (options?: { reset?: boolean; bindError?: boolean }) =>
    buildAuthPageUrl({
      origin: window.location.origin,
      linkId,
      resumeRaw: resumeRawRef.current,
      reset: options?.reset,
      fromClaim: fromClaimRef.current,
      bindError: options?.bindError,
    });

  /**
   * An active Clerk session whose bind failed (e.g. consumer org membership
   * quota) would keep this page on the binding view forever. Drop it so the
   * user lands back on the form with the bind error shown.
   */
  const signOutAfterBindFailure = async () => {
    if (bindFailedRef.current) return;
    bindFailedRef.current = true;
    try {
      clearConsumerIdentityBind();
      await clerk.signOut({ redirectUrl: authPageUrl({ bindError: true }) });
    } catch (signOutError) {
      console.error("[AuthPage] Failed to clear session after bind:", signOutError);
      bindFailedRef.current = false;
      setBinding(false);
      if (isAxiosNetworkFailure(signOutError)) return;
      setError(t("auth.errorBind"));
    }
  };

  /**
   * Clerk keeps a session in `pending` until every session task configured on
   * the instance (forced organization selection, MFA setup, password reset) is
   * resolved, and reports it as signed out. The widget cannot resolve tasks —
   * the backend owns consumer org membership — and single-session mode makes
   * any new sign-in attempt fail while that session lingers, so the only way
   * forward is to drop it and let the user start over.
   */
  const resetPendingSession = async () => {
    if (resetPendingRef.current) return;
    resetPendingRef.current = true;
    console.warn(
      "[AuthPage] Clerk session is pending; unresolved task:",
      clerkSession?.currentTask?.key,
    );

    if (searchParams.get(AUTH_RESET_PARAM) === "1") {
      setError(t("auth.errorPendingSession"));
      return;
    }

    try {
      clearConsumerIdentityBind();
      await clerk.signOut({ redirectUrl: authPageUrl({ reset: true }) });
    } catch (signOutError) {
      console.error("[AuthPage] Failed to clear pending session:", signOutError);
      setError(t("auth.errorPendingSession"));
    }
  };

  useEffect(() => {
    persistAuthMode(mode);
  }, [mode]);

  useEffect(() => {
    if (!ready) return;
    if (sessionStatus === "active") {
      void finishBind();
      return;
    }
    if (sessionStatus === "pending") {
      void resetPendingSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per Clerk session transition
  }, [ready, clerkSession?.id, sessionStatus]);

  const ssoRedirectUrls = () => {
    // linkId, resume, and from=claim must survive the IdP round-trip. Clerk
    // owns the redirect back; a cancel otherwise lands on the Account Portal.
    return {
      redirectUrl: ssoCallbackUrl({
        origin: window.location.origin,
        linkId,
        resumeRaw: resumeRawRef.current,
        fromClaim: fromClaimRef.current,
      }),
      redirectUrlComplete: authPageUrl(),
    };
  };

  const handleEmailContinue = async () => {
    if (!ready || busy || !email.trim() || !signIn || !signUp) return;
    // iOS only raises the keyboard for a focus() inside the tap. The OTP input
    // mounts after the await, so hold the keyboard on a proxy it can take over.
    keyboardProxyRef.current?.focus({ preventScroll: true });
    setBusy(true);
    setError(null);
    try {
      const nextMode = await startEmailSignInOrUp({
        email,
        signIn,
        signUp,
      });
      setMode(nextMode);
      persistAuthMode(nextMode);
      setStep("code");
      startResendCooldown();
    } catch (flowError) {
      console.error("[AuthPage] Email auth failed:", flowError);
      setError(authErrorMessage((key) => t(key), flowError));
      emailInputRef.current?.focus({ preventScroll: true });
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async () => {
    if (!ready || busy || code.length !== OTP_LENGTH || !signIn || !signUp) return;
    persistAuthMode(mode);
    setBusy(true);
    setError(null);
    try {
      if (mode === "signIn") {
        const result = await signIn.attemptFirstFactor({
          strategy: "email_code",
          code: code.trim(),
        });
        if (result.status === "complete" && result.createdSessionId) {
          await setSignInActive?.({
            session: result.createdSessionId,
            redirectUrl: authPageUrl(),
          });
        } else {
          throw new Error(`sign_in_${result.status ?? "incomplete"}`);
        }
      } else {
        const result = await signUp.attemptEmailAddressVerification({
          code: code.trim(),
        });
        if (result.status === "complete" && result.createdSessionId) {
          await setSignUpActive?.({
            session: result.createdSessionId,
            redirectUrl: authPageUrl(),
          });
        } else {
          // `missing_requirements` means the instance asks for attributes this
          // flow never collects (a password, for instance). Naming them keeps
          // the failure traceable to the Clerk setting behind it.
          const missing = (result.missingFields ?? []).join("|");
          throw new Error(
            `sign_up_${result.status ?? "incomplete"}${
              missing ? `:${missing}` : ""
            }`,
          );
        }
      }
    } catch (flowError) {
      console.error("[AuthPage] Verification failed:", flowError);
      setError(authErrorMessage((key) => t(key), flowError));
      setBusy(false);
    }
  };

  const handleResend = async () => {
    if (!ready || busy || !canResend || !signIn || !signUp) return;
    setBusy(true);
    setError(null);
    try {
      await resendEmailOtp({ mode, signIn, signUp });
      startResendCooldown();
    } catch (flowError) {
      console.error("[AuthPage] OTP resend failed:", flowError);
      setError(authErrorMessage((key) => t(key), flowError));
    } finally {
      setBusy(false);
    }
  };

  const handleSso = async (strategy: "oauth_google" | "oauth_microsoft") => {
    if (!ready || busy || !signIn) return;
    persistAuthMode("signIn");
    setBusy(true);
    setError(null);
    try {
      // Always start as sign-in. Clerk transfers an unknown IdP account
      // to sign-up; SsoCallbackPage keeps linkId / resume on every outcome.
      await signIn.authenticateWithRedirect({
        strategy,
        ...ssoRedirectUrls(),
      });
    } catch (ssoError) {
      console.error(`[AuthPage] ${strategy} redirect failed:`, ssoError);
      setError(authErrorMessage((key) => t(key), ssoError));
      setBusy(false);
    }
  };

  if (binding || (ready && sessionStatus === "active")) {
    return (
      <AuraLayout colorConfig={colorConfig}>
        <Layout.Content>
          <div className="landing-section content-center loader-page">
            <section className="content">
              {!error && (
                <div
                  className="loader-spinner"
                  role="status"
                  aria-label={t(bindingMessageKey())}
                />
              )}
              <BodyCopy textColor={TITLE_COLOR}>
                {t(bindingMessageKey())}
              </BodyCopy>
              {error && (
                <BodyCopy
                  textColor={errorInk}
                  className="consumer-auth-error"
                  style={{ color: errorInk }}
                >
                  {error}
                </BodyCopy>
              )}
            </section>
          </div>
        </Layout.Content>
      </AuraLayout>
    );
  }

  const isCode = step === "code";
  const showBack = isCode || fromClaimRef.current;

  const handleBack = () => {
    if (isCode) {
      setStep("form");
      setCode("");
      setError(null);
      resetResendCooldown();
      return;
    }
    redirectTo(APP_ROUTES.CLAIM_ENTRY_PAGE);
  };

  return (
    <AuraLayout colorConfig={colorConfig}>
      {showBack && (
        <Layout.Header>
          <Navbar onBackClick={handleBack} />
        </Layout.Header>
      )}
      <Layout.Content className="landing-section consumer-auth-section">
        <ScrollFadeIndicator color={bodyCopy} />
        <section className="consumer-auth-form">
          <header className="consumer-auth-header">
            <GradientText as="h2" textColor={TITLE_COLOR}>
              <Trans i18nKey={isCode ? "auth.titleCode" : "auth.title"} />
            </GradientText>
            {!isCode && (
              <BodyCopy as="p" textColor={bodyCopy}>
                <Trans i18nKey="auth.subtitle" />
              </BodyCopy>
            )}
          </header>

          <div className="consumer-auth-main">
            {!isCode && (
              <>
                <div className="consumer-auth-socials">
                  <SocialAuthButton
                    provider="google"
                    label={t("auth.google")}
                    textColor={TITLE_COLOR}
                    background={background}
                    disabled={busy}
                    onClick={() => void handleSso("oauth_google")}
                  />
                  <SocialAuthButton
                    provider="microsoft"
                    label={t("auth.microsoft")}
                    textColor={TITLE_COLOR}
                    background={background}
                    disabled={busy}
                    onClick={() => void handleSso("oauth_microsoft")}
                  />
                </div>
                <div className="consumer-auth-or" style={{ color: footerColor }}>
                  <BodyCopy
                    as="span"
                    textColor={footerColor}
                    className="consumer-auth-or-label"
                  >
                    {t("auth.or")}
                  </BodyCopy>
                </div>
              </>
            )}

            {isCode ? (
              <div className="otp-code-field">
                <BodyCopy
                  as="p"
                  textColor={error ? errorInk : footerColor}
                  className={`otp-code-hint${error ? " is-error" : ""}`}
                  style={error ? { color: errorInk } : undefined}
                >
                  {error ?? <Trans i18nKey="auth.codeHint" />}
                </BodyCopy>
                <OtpCodeInput
                  value={code}
                  onChange={(next) => {
                    setCode(next);
                    if (error) setError(null);
                  }}
                  disabled={busy}
                  autoFocus
                  textColor={bodyCopy}
                  borderGradient={backgroundBtn}
                  fillColor={background}
                  invalid={Boolean(error)}
                  errorColor={errorColor}
                  ariaLabel={t("auth.code")}
                />
                <div className="otp-resend">
                  {canResend ? (
                    <button
                      type="button"
                      className="consumer-auth-switch"
                      disabled={busy}
                      onClick={() => void handleResend()}
                    >
                      <GradientText
                        as="span"
                        textColor={backgroundBtn}
                        className="consumer-auth-switch-action"
                      >
                        {t("auth.resendCode")}
                      </GradientText>
                    </button>
                  ) : (
                    <BodyCopy
                      as="p"
                      textColor={footerColor}
                      className="otp-resend-wait"
                    >
                      {t("auth.resendIn", {
                        time: formatResendClock(resendRemaining),
                      })}
                    </BodyCopy>
                  )}
                </div>
              </div>
            ) : (
              <div className="mock-card-field">
                <FloatingField
                  label={t("auth.email")}
                  placeholder={t("auth.emailPlaceholder")}
                  value={email}
                  colors={fieldColors}
                >
                  <input
                    ref={emailInputRef}
                    id="consumer-auth-email"
                    style={{ color: bodyCopy }}
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </FloatingField>
              </div>
            )}

            {error && !isCode && (
              <BodyCopy
                as="p"
                textColor={errorInk}
                className="consumer-auth-error"
                style={{ color: errorInk }}
              >
                {error}
              </BodyCopy>
            )}

            {/* Target for Clerk bot protection. Without it, sign-up attempts on an
                instance with CAPTCHA enabled are rejected before reaching us. */}
            <div id="clerk-captcha" />
          </div>
        </section>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section consumer-auth-footer" ref={verifyCtaRef}>
          <input
            ref={keyboardProxyRef}
            className="consumer-auth-keyboard-proxy"
            type="text"
            inputMode="numeric"
            tabIndex={-1}
            aria-hidden
          />
          <Button
            size="large"
            background={backgroundBtn}
            color={textColorBtn}
            onClick={() =>
              void (isCode ? handleVerify() : handleEmailContinue())
            }
            disabled={
              busy || (isCode ? code.length !== OTP_LENGTH : !email.trim())
            }
          >
            {isCode ? t("auth.verify") : t("auth.continue")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
